import type { ComponentType } from "react";
import {
  Baby,
  Cog,
  Cpu,
  Hammer,
  Hospital,
  Layers,
  Microscope,
  Printer,
  Puzzle,
  Scale,
  ShieldCheck,
  Skull,
  Sparkles,
  Stethoscope,
  Syringe,
} from "lucide-react";
import {
  IconeAparelhosOrtodonticos,
  IconeModelos,
  IconePlacas,
  IconePlanejamentoDigital,
  IconeProteseSobreDente,
  IconeProteseSobreImplante,
  IconeProtesesProvisorias,
  IconeProtesesTotais,
} from "./icones-do-laboratorio";
import {
  IconeAcupuntura,
  IconeCirurgiaEstetica,
  IconeDentistica,
  IconeDtm,
  IconeEndodontia,
  IconeEsporte,
  IconeEstomatologia,
  IconeHomeopatia,
  IconeNecessidadesEspeciais,
  IconeOdontogeriatria,
  IconePeriodontia,
  IconeProteseBucomaxilo,
  IconeRadiologia,
  IconeSaudeColetiva,
  IconeTrabalho,
} from "./icones-das-especialidades";
import { imagemDoIcone } from "./imagens-dos-icones";

/** Ícone da lucide ou um dos desenhados pra as categorias do laboratório e as especialidades. */
export type Icone = ComponentType<{ className?: string }>;
// Com PNG própria (apps/app/public/icones), `imagem` vence o ícone; ele fica como reserva.
export type IconeComCor = { icone: Icone; cor: string; imagem?: string };

// Pela palavra do nome, na ordem: "Próteses sobre implantes" é implante antes de ser prótese, e
// "Prótese Bucomaxilofacial" é prótese antes de ser cirurgia. Serve às categorias do laboratório,
// às da clínica e às especialidades.
const REGRAS: [RegExp, Icone, string][] = [
  [/planejamento|guia/i, IconePlanejamentoDigital, "#8b5cf6"],
  [/implant/i, IconeProteseSobreImplante, "#0ea5e9"],
  [/harmoniza/i, Syringe, "#d946ef"],
  [/pr[óo]tese bucomaxilo/i, IconeProteseBucomaxilo, "#0891b2"],
  [/cirurgia est[ée]tica/i, IconeCirurgiaEstetica, "#f472b6"],
  [/bucomaxilo|cirurgia/i, Skull, "#ef4444"],
  [/fixas|prótese dent|sobre dente/i, IconeProteseSobreDente, "#f59e0b"],
  [/dent[íi]stica/i, IconeDentistica, "#ec4899"],
  [/restaura|est[ée]tic/i, Sparkles, "#ec4899"],
  [/totais|remov[íi]v/i, IconeProtesesTotais, "#10b981"],
  [/geriatr/i, IconeOdontogeriatria, "#10b981"],
  [/parciais|ppr/i, Puzzle, "#14b8a6"],
  [/dtm|temporomandibular/i, IconeDtm, "#6366f1"],
  [/placa/i, IconePlacas, "#6366f1"],
  [/ortoped/i, IconeProtesesTotais, "#fb923c"],
  [/ortod[oô]n/i, IconeAparelhosOrtodonticos, "#f97316"],
  [/modelo/i, IconeModelos, "#64748b"],
  [/provis/i, IconeProtesesProvisorias, "#eab308"],
  [/cad|digital/i, Cpu, "#2563eb"],
  [/impress|3d/i, Printer, "#a855f7"],
  [/fresag|usinag/i, Cog, "#0891b2"],
  [/reparo|manuten/i, Hammer, "#78716c"],
  [/endodon/i, IconeEndodontia, "#dc2626"],
  [/periodon/i, IconePeriodontia, "#e11d48"],
  [/pediatr/i, Baby, "#f472b6"],
  [/consulta|cl[íi]nico/i, Stethoscope, "#3b82f6"],
  [/necessidades especiais/i, IconeNecessidadesEspeciais, "#0d9488"],
  [/trabalho/i, IconeTrabalho, "#78716c"],
  [/hospitalar/i, Hospital, "#ef4444"],
  [/legal/i, Scale, "#475569"],
  [/coletiva/i, IconeSaudeColetiva, "#22c55e"],
  [/patolog/i, Microscope, "#7c3aed"],
  [/preven/i, ShieldCheck, "#22c55e"],
  [/estomato/i, IconeEstomatologia, "#0ea5e9"],
  [/diagn|radiolog|imagin/i, IconeRadiologia, "#0284c7"],
  [/esporte/i, IconeEsporte, "#84cc16"],
  [/acupuntura/i, IconeAcupuntura, "#06b6d4"],
  [/homeopatia|terapia/i, IconeHomeopatia, "#16a34a"],
];

const RESERVA = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#6366f1"];

/** Ícone e cor de uma categoria (ou especialidade) pelo nome; sem regra, cor estável pelo nome. */
export function iconeDaCategoria(nome: string): IconeComCor {
  const regra = REGRAS.find(([re]) => re.test(nome));
  const imagem = imagemDoIcone(nome);
  if (regra) return { icone: regra[1], cor: regra[2], imagem };
  const soma = [...nome].reduce((n, c) => n + c.charCodeAt(0), 0);
  return { icone: Layers, cor: RESERVA[soma % RESERVA.length], imagem };
}
